import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtError, eur } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ShoppingCart, Plus, Minus, Trash2, Truck, FileText, AlertTriangle, Save, FolderOpen } from "lucide-react";
import { toast } from "sonner";

const COMMUNES = ["Baie-Mahault", "Pointe-à-Pitre", "Les Abymes", "Le Gosier", "Petit-Bourg", "Lamentin",
  "Goyave", "Morne-à-l'Eau", "Sainte-Anne", "Sainte-Rose", "Capesterre-Belle-Eau", "Le Moule", "Petit-Canal",
  "Port-Louis", "Saint-François", "Trois-Rivières", "Anse-Bertrand", "Baillif", "Basse-Terre", "Bouillante",
  "Deshaies", "Gourbeyre", "Pointe-Noire", "Saint-Claude", "Vieux-Fort", "Vieux-Habitants"];

export default function Simulator() {
  const { territoire } = useApp();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState([]);
  const [famille, setFamille] = useState("");
  const [cart, setCart] = useState([]); // {reference, produit, prix_vente, cout, unite, quantite}
  const [commune, setCommune] = useState("");
  const [zone, setZone] = useState(null);
  const [manutention, setManutention] = useState(0);
  const [urgence, setUrgence] = useState(false);
  const [multisite, setMultisite] = useState(false);
  const [sugTonnage, setSugTonnage] = useState("");
  const [sugVolume, setSugVolume] = useState("");
  const [suggestion, setSuggestion] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveCode, setSaveCode] = useState("");
  const [resumeCode, setResumeCode] = useState("");

  useEffect(() => {
    api.get("/price-book").then((r) => {
      setCatalog(r.data.filter((p) => p.famille !== "Transport / Livraison"));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!commune) { setZone(null); return; }
    api.get("/transport/zone", { params: { commune } }).then((r) => setZone(r.data)).catch(() => setZone(null));
  }, [commune]);

  const familles = useMemo(() => [...new Set(catalog.map((p) => p.famille))], [catalog]);
  const items = catalog.filter((p) => !famille || p.famille === famille);

  const addItem = (p) => {
    setCart((c) => {
      const ex = c.find((x) => x.reference === p.reference);
      if (ex) return c.map((x) => x.reference === p.reference ? { ...x, quantite: x.quantite + 1 } : x);
      return [...c, { reference: p.reference, produit: p.produit, prix_vente: p.prix_vente_ht || p.prix_ht,
        cout: p.prix_vente_ht ? p.prix_ht : p.prix_ht, unite: p.unite, quantite: 1 }];
    });
  };
  const setQty = (ref, d) => setCart((c) => c.map((x) => x.reference === ref ? { ...x, quantite: Math.max(1, x.quantite + d) } : x));
  const remove = (ref) => setCart((c) => c.filter((x) => x.reference !== ref));

  const suggestVehicle = async () => {
    if (!sugTonnage && !sugVolume) return;
    try {
      const { data } = await api.post("/transport/suggest", {
        tonnage_t: parseFloat(sugTonnage) || 0, volume_m3: parseFloat(sugVolume) || 0,
      });
      setSuggestion(data);
    } catch (e) { toast.error(fmtError(e)); }
  };

  const totals = useMemo(() => {
    let ht = cart.reduce((s, x) => s + x.prix_vente * x.quantite, 0);
    if (urgence) ht *= 1.08;
    const livraison = zone?.prix_teca_ht || 0;
    const manu = manutention * 150;
    const totalHt = ht + livraison + manu;
    const taux = territoire === "Guyane" ? 0 : 8.5;
    return { matos: ht, livraison, manu, ht: totalHt, tva: totalHt * taux / 100, ttc: totalHt * (1 + taux / 100), taux };
  }, [cart, zone, manutention, urgence, territoire]);

  const createQuote = async () => {
    setSaving(true);
    try {
      const items = cart.map((x) => ({
        designation: x.produit + (urgence ? " [URGENCE <48h +8%]" : ""),
        unite: x.unite, quantite: x.quantite,
        prix_achat: urgence ? Math.round(x.prix_vente / 1.08 * 100) / 100 : x.cout,
        marge_pct: x.cout > 0 && x.prix_vente > x.cout ? Math.round((x.prix_vente / x.cout - 1) * 1000) / 10 : 0,
      }));
      if (zone?.prix_teca_ht) items.push({
        designation: zone.ligne_devis, unite: "forfait", quantite: 1,
        prix_achat: zone.cout_interne_ht ?? zone.prix_teca_ht, marge_pct: zone.cout_interne_ht ? Math.round((zone.prix_teca_ht / zone.cout_interne_ht - 1) * 1000) / 10 : 0,
      });
      if (manutention > 0) items.push({
        designation: "Manutention complémentaire au-delà de 20 minutes sur site - tarif horaire TECA (exécution : ETPS).",
        unite: "heure", quantite: manutention, prix_achat: 120, marge_pct: 25,
      });
      if (multisite) items.push({
        designation: "Supplément multi-sites — enlèvement chez plusieurs fournisseurs avec calcul d'itinéraire optimisé. Montant à chiffrer selon la tournée.",
        unite: "forfait", quantite: 1, prix_achat: 0, marge_pct: 0,
      });
      const { data } = await api.post("/quotes", {
        territoire, objet: `Simulation commande${commune ? ` — ${commune}` : ""}`,
        conditions: "Tarif sous réserve de validation du poids, volume, nature de la marchandise, accessibilité et capacité du véhicule requis. Les conditions d'achat négociées entre TECA SOLUTIONS TP et ses prestataires restent strictement internes.",
        items,
      });
      toast.success(`Devis ${data.numero} créé depuis la simulation`);
      navigate(`/devis/${data.id}`);
    } catch (e) { toast.error(fmtError(e)); } finally { setSaving(false); }
  };

  const saveSim = async () => {
    try {
      const { data } = await api.post("/simulations", { code: saveCode || undefined, data: { cart, commune, manutention, urgence, multisite } });
      setSaveCode(data.code);
      toast.success(`Simulation sauvegardée — code ${data.code}`);
    } catch (e) { toast.error(fmtError(e)); }
  };

  const resumeSim = async () => {
    if (!resumeCode.trim()) return;
    try {
      const { data } = await api.get(`/simulations/${resumeCode.trim()}`);
      const d = data.data || {};
      setCart(d.cart || []);
      setCommune(d.commune || "");
      setManutention(d.manutention || 0);
      setUrgence(!!d.urgence);
      setMultisite(!!d.multisite);
      setSaveCode(data.code);
      toast.success(`Simulation ${data.code} reprise`);
    } catch (e) { toast.error(fmtError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="simulator-page">
      <div>
        <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight">Simulateur de commande</h1>
        <p className="text-sm text-muted-foreground">Matériaux & engins au prix TECA publié + livraison zonée ETPS (Guadeloupe). Conversion directe en devis.</p>
      </div>

      <div className="flex gap-2 items-center flex-wrap" data-testid="sim-savebar">
        <Input data-testid="sim-resume-code" placeholder="Code de reprise" value={resumeCode}
          onChange={(e) => setResumeCode(e.target.value.toUpperCase())} className="w-44 h-12 font-mono-num uppercase" maxLength={8} />
        <Button data-testid="sim-resume" onClick={resumeSim} variant="outline" className="h-12 font-bold">
          <FolderOpen className="h-4 w-4 mr-1" /> Reprendre
        </Button>
        <Button data-testid="sim-save" onClick={saveSim} variant="outline" className="h-12 font-bold">
          <Save className="h-4 w-4 mr-1" /> Sauvegarder
        </Button>
        {saveCode && <span className="text-sm font-mono-num font-bold bg-amber-100 dark:bg-amber-950 px-3 py-2 rounded-lg" data-testid="sim-save-code">Code : {saveCode} — valable 30 jours</span>}
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="space-y-3">
          <div className="flex gap-2 flex-wrap" data-testid="sim-familles">
            <button onClick={() => setFamille("")} data-testid="sim-famille-tous"
              className={`px-3 py-2 rounded-lg text-xs font-bold border-2 ${!famille ? "bg-amber-500 text-slate-900 border-amber-500" : "bg-card border-border"}`}>Tous</button>
            {familles.map((f) => (
              <button key={f} onClick={() => setFamille(f)} data-testid={`sim-famille-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`px-3 py-2 rounded-lg text-xs font-bold border-2 ${famille === f ? "bg-amber-500 text-slate-900 border-amber-500" : "bg-card border-border"}`}>{f}</button>
            ))}
          </div>
          <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1" data-testid="sim-catalog">
            {items.map((p) => (
              <div key={p.id} className="bg-card rounded-xl border border-border p-3 flex items-center gap-3" data-testid={`sim-product-${p.reference}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm leading-tight">{p.produit}</div>
                  <div className="text-xs text-muted-foreground">{p.fournisseur} — {p.unite}</div>
                </div>
                <div className="font-mono-num font-bold text-sm">{eur(p.prix_vente_ht || p.prix_ht)}</div>
                <Button size="icon" data-testid={`sim-add-${p.reference}`} onClick={() => addItem(p)} className="bg-amber-500 text-slate-900 hover:bg-amber-400 h-10 w-10 shrink-0">
                  <Plus className="h-5 w-5" />
                </Button>
              </div>
            ))}
            {items.length === 0 && <p className="text-sm text-muted-foreground">Aucun article dans le price book.</p>}
          </div>
        </div>

        <div className="space-y-3">
          <div className="bg-card rounded-xl border border-border p-4 space-y-3" data-testid="sim-cart">
            <div className="flex items-center gap-2 font-display font-bold text-lg uppercase"><ShoppingCart className="h-5 w-5 text-amber-600" /> Commande</div>
            {cart.length === 0 && <p className="text-sm text-muted-foreground" data-testid="sim-cart-empty">Ajoutez des articles depuis le catalogue.</p>}
            {cart.map((x) => (
              <div key={x.reference} className="flex items-center gap-2 text-sm" data-testid={`sim-cart-${x.reference}`}>
                <div className="flex-1 min-w-0 truncate font-semibold">{x.produit}</div>
                <div className="flex items-center gap-1">
                  <button data-testid={`sim-minus-${x.reference}`} onClick={() => setQty(x.reference, -1)} className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center"><Minus className="h-4 w-4" /></button>
                  <span className="w-8 text-center font-mono-num font-bold">{x.quantite}</span>
                  <button data-testid={`sim-plus-${x.reference}`} onClick={() => setQty(x.reference, 1)} className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center"><Plus className="h-4 w-4" /></button>
                </div>
                <div className="w-20 text-right font-mono-num font-bold">{eur(x.prix_vente * x.quantite)}</div>
                <button data-testid={`sim-remove-${x.reference}`} onClick={() => remove(x.reference)} className="text-red-500 p-1"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            {cart.length > 0 && (
              <>
              <label className="flex items-center gap-2 text-sm font-semibold pt-1">
                <input type="checkbox" data-testid="sim-urgence" checked={urgence} onChange={(e) => setUrgence(e.target.checked)} />
                Urgence &lt;48 h (+8 % — règle SENSIEH, sous réserve de disponibilité)
              </label>
              <label className="flex items-center gap-2 text-sm font-semibold">
                <input type="checkbox" data-testid="sim-multisite" checked={multisite} onChange={(e) => setMultisite(e.target.checked)} />
                Enlèvement chez plusieurs fournisseurs (multi-sites — itinéraire optimisé, supplément à chiffrer)
              </label>
              </>
            )}
          </div>

          <div className="bg-card rounded-xl border border-border p-4 space-y-3" data-testid="sim-delivery">
            <div className="flex items-center gap-2 font-display font-bold text-lg uppercase"><Truck className="h-5 w-5 text-amber-600" /> Livraison ETPS</div>
            <select data-testid="sim-commune" value={commune} onChange={(e) => setCommune(e.target.value)}
              className="w-full h-12 rounded-lg border border-input bg-background px-3 text-sm font-semibold">
              <option value="">— Commune de livraison —</option>
              {COMMUNES.map((c) => <option key={c}>{c}</option>)}
            </select>
            {zone && zone.zone && (
              <div className="bg-amber-50 dark:bg-amber-950 border border-amber-400 rounded-lg p-3 text-sm" data-testid="sim-zone-result">
                <div className="font-bold">Zone {zone.zone} — {eur(zone.prix_teca_ht)} HT</div>
                <div className="text-xs mt-1 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5 text-amber-600" /> Conformité ETPS : À CONTRÔLER avant exécution.</div>
              </div>
            )}
            {zone && !zone.zone && <div className="text-sm font-semibold text-amber-700" data-testid="sim-zone-none">{zone.message}</div>}
            <div className="flex items-center gap-2">
              <label className="text-sm font-semibold flex-1">Manutention (&gt;20 min sur site) — heures × 150 € HT</label>
              <Input data-testid="sim-manutention" type="number" min="0" step="0.5" value={manutention} onChange={(e) => setManutention(parseFloat(e.target.value) || 0)} className="w-24 h-11" />
            </div>

            <div className="border-t border-border pt-3 space-y-2">
              <div className="text-sm font-bold">Suggestion de véhicule (flotte TECA)</div>
              <div className="flex gap-2">
                <Input data-testid="sim-sug-tonnage" type="number" step="any" placeholder="Tonnage (t)" value={sugTonnage} onChange={(e) => setSugTonnage(e.target.value)} className="h-11" />
                <Input data-testid="sim-sug-volume" type="number" step="any" placeholder="Volume (m³)" value={sugVolume} onChange={(e) => setSugVolume(e.target.value)} className="h-11" />
                <Button type="button" data-testid="sim-sug-run" onClick={suggestVehicle} variant="outline" className="h-11 font-bold shrink-0">Suggérer</Button>
              </div>
              {suggestion && (
                <div className="bg-emerald-50 dark:bg-emerald-950 border border-emerald-400 rounded-lg p-3 text-sm" data-testid="sim-suggestion">
                  <div className="font-bold">
                    {suggestion.recommandation.nom}
                    {suggestion.optimal && <span className="ml-2 bg-emerald-600 text-white text-[10px] font-bold px-2 py-0.5 rounded uppercase">Recommandé</span>}
                  </div>
                  <div className="text-xs mt-0.5">
                    Charge utile {suggestion.recommandation.charge_utile_t} t · PTAC {suggestion.recommandation.ptac_t} t ·{" "}
                    {suggestion.recommandation.rotations} rotation(s) · utilisation {suggestion.recommandation.utilisation_pct} %
                    {suggestion.recommandation.prix_base_ht != null && ` · dès ${suggestion.recommandation.prix_base_ht} € HT`}
                  </div>
                  {suggestion.alternatives?.length > 0 && (
                    <div className="text-xs mt-1 text-muted-foreground">
                      Alternatives : {suggestion.alternatives.map((a) => `${a.nom} (${a.rotations} rot.)`).join(" · ")}
                    </div>
                  )}
                  <div className="text-[10px] mt-1 text-muted-foreground">{suggestion.note}</div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-teca-900 text-white rounded-xl p-5 space-y-1.5" data-testid="sim-totals">
            {totals.livraison > 0 && <div className="flex justify-between text-sm"><span className="text-slate-400">Livraison zone</span><span className="font-mono-num">{eur(totals.livraison)}</span></div>}
            {totals.manu > 0 && <div className="flex justify-between text-sm"><span className="text-slate-400">Manutention</span><span className="font-mono-num">{eur(totals.manu)}</span></div>}
            <div className="flex justify-between text-sm"><span className="text-slate-400">Total HT</span><span className="font-mono-num font-bold" data-testid="sim-total-ht">{eur(totals.ht)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-slate-400">TVA {totals.taux}%</span><span className="font-mono-num font-bold">{eur(totals.tva)}</span></div>
            <div className="flex justify-between text-xl border-t border-amber-500 pt-2"><span className="font-display font-bold uppercase">Total TTC</span><span className="font-mono-num font-extrabold text-amber-400" data-testid="sim-total-ttc">{eur(totals.ttc)}</span></div>
          </div>

          <Button data-testid="sim-create-quote" onClick={createQuote} disabled={saving || cart.length === 0}
            className="w-full h-14 text-base bg-amber-500 text-slate-900 font-bold hover:bg-amber-400">
            <FileText className="h-5 w-5 mr-2" /> {saving ? "CRÉATION…" : "CONVERTIR EN DEVIS"}
          </Button>
          <p className="text-[10px] text-muted-foreground">Coûts fournisseurs et marges internes : visibilité ADMIN TECA uniquement, jamais exportés côté client.</p>
        </div>
      </div>
    </div>
  );
}
